import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { SortableItemBase, SortableVariant } from '../types';

/**
 * ドラッグハンドルに必要なprops
 */
type DragHandleProps = {
  /** ドラッグハンドルのHTML属性 */
  attributes: React.HTMLAttributes<HTMLElement>;
  /** ドラッグハンドルのイベントリスナー */
  listeners?: React.DOMAttributes<HTMLElement>;
};

/**
 * SortableItemのprops
 * @template T - SortableItemBaseを拡張するアイテム型
 */
type Props<T extends SortableItemBase> = {
  /** ソート対象のアイテム */
  item: T;
  /** アイテムをレンダリングする関数 */
  renderItem: (item: T, dragHandleProps: DragHandleProps) => React.ReactNode;
  /**
   * 描画バリアント。デフォルト: `'list'`
   * - `'list'`: `<div>` でアイテムをラップする（既存互換）
   * - `'table'`: `<tr>` でアイテムをラップする。必ず `<Table>` の `<tbody>` 内に配置すること
   */
  variant?: SortableVariant;
};

/**
 * ドラッグ&ドロップ可能な個別アイテムコンポーネント
 *
 * NOTE: React.FCはジェネリクスを直接サポートしないため、React.FC型を使用せずアロー関数の引数にPropsを指定して型定義しています
 *
 * @template T - SortableItemBaseを拡張するアイテム型
 * @param {Props<T>} props - コンポーネントのprops
 * @param {T} props.item - ソート対象のアイテム
 * @param {function} props.renderItem - アイテムをレンダリングする関数
 * @param {SortableVariant} [props.variant='list'] - 描画バリアント（'list' | 'table'）
 * @returns {JSX.Element} ドラッグ&ドロップ可能なアイテム
 */
export const SortableItem = <T extends SortableItemBase>({
  item,
  renderItem,
  variant = 'list',
}: Props<T>) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (variant === 'table') {
    return (
      <tr ref={setNodeRef} style={style}>
        {renderItem(item, { attributes, listeners })}
      </tr>
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      {renderItem(item, { attributes, listeners })}
    </div>
  );
};
